import { JSX, useId, useRef, useState } from 'react'

import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { Check, ChevronDown, X } from 'lucide-react'
import { Controller, FieldValues, Path } from 'react-hook-form'

import {
  DISABLED_INPUT_CLASSNAME,
  DisabledReasonTooltip,
  Group,
  Tooltip,
} from '~/components/component-library'
import {
  matchesQuery,
  optionText,
} from '~/components/component-library/ComboboxInput/utils'
import { areOptionValuesEqual } from '~/components/component-library/SelectInput/util'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { cn } from '~/utils/tailwind-utils'

import type {
  ComboboxInputInnerProps,
  ComboboxInputProps,
} from '~/components/component-library/ComboboxInput/types'

const toValues = <ValueType,>(value: ValueType | ValueType[] | undefined) =>
  value === undefined || value === null || value === ''
    ? []
    : Array.isArray(value)
      ? value
      : [value]

export const ComboboxInput = <
  ValueType = unknown,
  FormType extends FieldValues = FieldValues,
>(
  props: ComboboxInputProps<ValueType, FormType>,
): JSX.Element => {
  const { optional, requiredErrorMessage, control, inputName, ...rest } = props
  const multiple = props.multiple ?? false

  // Single mode hands back the one value; multiple hands back the array.
  const emit = (values: ValueType[]) => {
    if (props.multiple) {
      props.selectValue?.(values)
      return values
    }
    if (values[0] !== undefined) props.selectValue?.(values[0])
    return values[0]
  }

  if (control || inputName) {
    return (
      <Controller
        control={control}
        name={inputName as Path<FormType>}
        rules={{
          validate: (value) =>
            optional ||
            toValues(value).length > 0 ||
            (requiredErrorMessage ?? 'Required'),
        }}
        render={({ field, fieldState, formState }) => {
          const selectedValues = toValues<ValueType>(
            field.value ?? props.selectedOption,
          )
          return (
            <ComboboxInputInner
              {...rest}
              multiple={multiple}
              error={visibleError(fieldState.error, {
                isTouched: fieldState.isTouched,
                isEmpty: selectedValues.length === 0,
                isSubmitted: formState.isSubmitted,
              })}
              selectedValues={selectedValues}
              onChange={(values) => {
                field.onChange(emit(values))
                field.onBlur()
              }}
              onBlur={field.onBlur}
            />
          )
        }}
      />
    )
  }

  return (
    <ComboboxInputInner
      {...rest}
      multiple={multiple}
      selectedValues={toValues<ValueType>(props.selectedOption)}
      onChange={emit}
    />
  )
}

const ComboboxInputInner = <ValueType,>({
  options,
  selectedValues,
  onChange,
  onBlur,
  multiple,
  placeholder,
  disabled,
  disabledReason,
  label,
  footer,
  description,
  tooltip,
  secondaryDisplay,
  size = 'base',
  emptyText = 'No matches',
  error,
}: ComboboxInputInnerProps<ValueType>) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const labelId = useId()

  const isSelected = (value: ValueType) =>
    selectedValues.some((selected) => areOptionValuesEqual(selected, value))
  // Pick order, so the last chip is the one Backspace removes.
  const selectedOptions = selectedValues.flatMap(
    (value) =>
      options.find((option) => areOptionValuesEqual(option.value, value)) ?? [],
  )
  const visibleOptions = options.filter((option) =>
    matchesQuery({ text: optionText(option), query }),
  )
  const iconSize = size === 'small' ? 'size-4' : 'size-5'

  const setOpenState = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }

  const toggle = (value: ValueType) => {
    if (!multiple) {
      onChange([value])
      setOpenState(false)
      return
    }
    onChange(
      isSelected(value)
        ? selectedValues.filter((v) => !areOptionValuesEqual(v, value))
        : [...selectedValues, value],
    )
    setQuery('')
  }

  // Closed single mode shows the pick in the input; open, the input is the query.
  const singleText = selectedOptions[0] ? optionText(selectedOptions[0]) : ''
  const inputValue = multiple || open ? query : singleText

  const field = (
    <Popover.Anchor asChild>
      <div
        ref={fieldRef}
        className={cn(
          'flex w-full flex-wrap items-center gap-1.5 rounded-lg ring-1 ring-inset transition',
          error
            ? 'ring-error focus-within:ring-2'
            : 'ring-default focus-within:ring-2 focus-within:ring-focus',
          disabled
            ? `${DISABLED_INPUT_CLASSNAME} pointer-events-none bg-surface-raised`
            : 'bg-surface-raised',
          {
            'py-2 pl-3.5 pr-3 text-base': size === 'base',
            'py-1 pl-3 pr-2 text-sm': size === 'small',
          },
        )}
        // Reopens after a pick that closed the list but kept focus here.
        onClick={() => {
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        {multiple &&
          selectedOptions.map((option, index) => (
            <span
              key={index.toString()}
              className="flex items-center gap-1 rounded-md bg-surface-muted py-0.5 pl-2 pr-1 text-sm"
            >
              {optionText(option)}
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${optionText(option)}`}
                className="rounded text-secondary hover:text-primary"
                onClick={(event) => {
                  event.stopPropagation()
                  toggle(option.value)
                  // The chip unmounts, so keep focus in the field.
                  inputRef.current?.focus()
                }}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        <Command.Input
          ref={inputRef}
          value={inputValue}
          onValueChange={(next) => {
            setQuery(next)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && !open) setOpen(true)
            if (
              event.key === 'Backspace' &&
              multiple &&
              query === '' &&
              selectedValues.length > 0
            ) {
              onChange(selectedValues.slice(0, -1))
            }
          }}
          disabled={disabled}
          placeholder={
            multiple && selectedOptions.length > 0
              ? ''
              : open && singleText
                ? singleText
                : placeholder
          }
          aria-labelledby={label ? labelId : undefined}
          className="min-w-16 flex-1 bg-transparent py-0.5 outline-none placeholder:text-placeholder"
        />
        <ChevronDown
          aria-hidden
          className={cn(
            'ml-auto flex-shrink-0 transition-transform duration-150',
            iconSize,
            error ? 'text-error' : 'text-placeholder',
            { 'rotate-180': open },
          )}
        />
      </div>
    </Popover.Anchor>
  )

  return (
    <Group
      label={label}
      footer={error ? <ErrorFooter message={error} /> : footer}
      description={description}
      tooltip={tooltip}
      secondaryDisplay={secondaryDisplay}
    >
      {label && (
        <span id={labelId} hidden>
          {label}
        </span>
      )}
      <DisabledReasonTooltip
        disabled={disabled}
        disabledReason={disabledReason}
        className={cn('relative flex w-full', {
          'cursor-not-allowed': disabled,
        })}
      >
        <Popover.Root open={open} onOpenChange={setOpenState}>
          {/* Filtering is ours; cmdk only drives keyboard highlight. */}
          <Command shouldFilter={false} loop className="w-full">
            {field}
            <Popover.Portal>
              <Popover.Content
                side="bottom"
                align="start"
                sideOffset={4}
                collisionPadding={8}
                // Focus stays in the input while the list is open.
                onOpenAutoFocus={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onInteractOutside={(event) => {
                  if (fieldRef.current?.contains(event.target as Node)) {
                    event.preventDefault()
                  }
                }}
                className={cn(
                  'anchored z-[99] p-1 outline-none',
                  'data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100',
                )}
                style={{ width: 'var(--radix-popover-trigger-width)' }}
              >
                <Command.List className="max-h-48 overflow-y-auto">
                  <Command.Empty className="px-2.5 py-2 text-sm text-secondary">
                    {emptyText}
                  </Command.Empty>
                  {visibleOptions.map((option, index) => {
                    const item = (
                      <Command.Item
                        key={index.toString()}
                        value={`${index}-${optionText(option)}`}
                        disabled={option.disabled}
                        onSelect={() => toggle(option.value)}
                        className={cn(
                          'flex w-full cursor-pointer select-none items-center gap-2 rounded-md outline-none transition-colors',
                          'data-[selected=true]:bg-surface-muted',
                          'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50',
                          size === 'small'
                            ? 'px-2 py-1.5 text-sm'
                            : 'px-2.5 py-2 text-base',
                        )}
                      >
                        {option.icon && (
                          <div className="flex-shrink-0">{option.icon}</div>
                        )}
                        <span className="flex-1 text-primary">
                          {option.label}
                        </span>
                        {option.suffix && (
                          <div className="flex-shrink-0">{option.suffix}</div>
                        )}
                        <Check
                          strokeWidth={2.25}
                          className={cn(
                            'flex-shrink-0 text-secondary',
                            size === 'small' ? 'size-3.5' : 'size-4',
                            { invisible: !isSelected(option.value) },
                          )}
                        />
                      </Command.Item>
                    )
                    return option.disabled && option.disabledReason ? (
                      <Tooltip
                        key={index.toString()}
                        triggerClassName="w-full cursor-not-allowed"
                        contentClassName="z-[100]"
                        content={option.disabledReason}
                      >
                        {item}
                      </Tooltip>
                    ) : (
                      item
                    )
                  })}
                </Command.List>
              </Popover.Content>
            </Popover.Portal>
          </Command>
        </Popover.Root>
      </DisabledReasonTooltip>
    </Group>
  )
}

export type { ComboboxInputProps } from '~/components/component-library/ComboboxInput/types'
